import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { toast } from "react-toastify";
import { useTimeSpentTracker } from "../../hooks/usage/useTimeSpentTracker.js";
import { endAiChat } from "../../api/sherpa/sherpaApi.js";
import { useSherpaSessions } from "../../hooks/sherpa/useSherpaSessions.js";
import {
  mapMessagesFromResponse,
  useSherpaChat,
} from "../../hooks/sherpa/useSherpaChat.js";
import SherpaLayout from "../../components/sherpa/SherpaLayout.jsx";
import SherpaSidebar from "../../components/sherpa/SherpaSidebar.jsx";
import SherpaHeader from "../../components/sherpa/SherpaHeader.jsx";
import ChatMessageList from "../../components/sherpa/ChatMessageList.jsx";
import ChatInput from "../../components/sherpa/ChatInput.jsx";
import MobileSessionDrawer from "../../components/sherpa/MobileSessionDrawer.jsx";
import SherpaDisclaimerView from "../../components/sherpa/SherpaDisclaimerView.jsx";
import CrisisReferralCard from "../../components/sherpa/CrisisReferralCard.jsx";
import { SidebarCol } from "../chatMain/Sidebar/Sidebar.jsx";

function ChatMain() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { sessionId: routeSessionId } = useParams();
  const [draft, setDraft] = useState("");
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [activeScreen, setActiveScreen] = useState("loading"); // "loading" | "disclaimer" | "chat" | "error"
  const [initErrorMessage, setInitErrorMessage] = useState(null);
  const initializedRef = useRef(false);

  const {
    sessions,
    currentSession,
    selectedSessionId,
    isLoadingSessions,
    isLoadingSessionMessages,
    createNewSession,
    selectSession,
    loadSessionMessages,
    updateSessionMessages,
    updateSessionMeta,
    populateArchivedSessions,
    fetchArchivedSessions,
    openArchivedSession,
    sessionsError,
  } = useSherpaSessions();

  const {
    isSending,
    sendMessage,
    startFreshChat,
    resumeChat,
    shouldClose,
    isCrisis,
    dismissCrisis,
    continueConversation,
    endConversation,
    checkInitialState,
  } = useSherpaChat({
    currentSession,
    updateSessionMessages,
    updateSessionMeta,
    onAuthError: () => navigate("/login"),
  });

  const [interruptedChatId, setInterruptedChatId] = useState(null);
  const [isInterruptedPromptOpen, setIsInterruptedPromptOpen] = useState(false);
  const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(false);
  const liveSessionIdRef = useRef(null);

  useTimeSpentTracker({
    interfaceName: "chat",
    flushIntervalMs: 15000,
    enabled: true,
  });

  const runInitialLoad = useCallback(async () => {
    setActiveScreen("loading");
    setInitErrorMessage(null);

    try {
      // Execute the strict sequential mobile lifecycle:
      // 1. POST /api/auth/check
      // 2. POST /api/session/state
      // 3. GET /api/sessions?token=...
      // 4. POST /api/chat/resume (if cached) or route to disclaimer / interrupted / start
      const result = await checkInitialState();

      if (result?.rawSessions) {
        populateArchivedSessions(result.rawSessions);
      }

      const hasAcceptedDisclaimer =
        localStorage.getItem("mindsherpa_disclaimer_accepted") === "true";

      if (result?.type === "disclaimer" || !hasAcceptedDisclaimer) {
        setActiveScreen("disclaimer");
        return;
      }

      if (result?.type === "interrupted") {
        setInterruptedChatId(result.interruptedChatId);
        setIsInterruptedPromptOpen(true);
        createNewSession({ intent: "auto" });
        setActiveScreen("chat");
        return;
      }

      if (result?.type === "resumed" || result?.type === "fresh") {
        const newId = createNewSession({
          intent: result.type === "resumed" ? "resume" : "fresh",
          resumeChatId: result.chatId,
        });
        const mapped = mapMessagesFromResponse(result.chatData);
        updateSessionMeta(newId, {
          remoteSessionId: result.chatId,
          dbSessionId:
            result.chatData?.db_session_id ||
            result.chatData?.dbSessionId ||
            null,
          idleTimeoutSeconds:
            result.chatData?.idle_timeout_seconds || 1800,
        });
        updateSessionMessages(newId, mapped);
        setActiveScreen("chat");
      }
    } catch (err) {
      console.error("Sherpa initial load failed:", err);
      const hasAcceptedDisclaimer =
        localStorage.getItem("mindsherpa_disclaimer_accepted") === "true";
      if (!hasAcceptedDisclaimer) {
        setActiveScreen("disclaimer");
        return;
      }
      setInitErrorMessage(
        err?.message ||
          "Unable to connect to the chat server. Please try again later."
      );
      setActiveScreen("error");
    }
  }, [
    checkInitialState,
    createNewSession,
    populateArchivedSessions,
    updateSessionMessages,
    updateSessionMeta,
  ]);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    runInitialLoad();
  }, [runInitialLoad]);

  const handleRetry = () => {
    initializedRef.current = false;
    runInitialLoad();
  };

  useEffect(() => {
    if (!selectedSessionId) return;
    loadSessionMessages(selectedSessionId);
  }, [loadSessionMessages, selectedSessionId]);

  useEffect(() => {
    if (!routeSessionId || activeScreen !== "chat") return;
    openArchivedSession(routeSessionId).catch((error) => {
      toast.error(error?.message || "Unable to load past session.");
    });
  }, [activeScreen, openArchivedSession, routeSessionId]);

  const handleBeginDisclaimer = async () => {
    try {
      localStorage.setItem("mindsherpa_disclaimer_accepted", "true");
      const newId = createNewSession({ intent: "fresh" });
      const { remoteSessionId, sessionData, mappedMessages } =
        await startFreshChat();
      updateSessionMeta(newId, {
        remoteSessionId,
        dbSessionId:
          sessionData?.db_session_id || sessionData?.dbSessionId || null,
        idleTimeoutSeconds: sessionData?.idle_timeout_seconds || 1800,
      });
      updateSessionMessages(newId, mappedMessages);
      setActiveScreen("chat");
    } catch (error) {
      toast.error(error?.message || "Unable to start Sherpa session.");
    }
  };

  const handleStartDemoMode = () => {
    const hasAcceptedDisclaimer =
      localStorage.getItem("mindsherpa_disclaimer_accepted") === "true";
    if (!hasAcceptedDisclaimer) {
      setActiveScreen("disclaimer");
      return;
    }
    const newId = createNewSession({ intent: "fresh" });
    updateSessionMessages(newId, [
      {
        id: `welcome-${Date.now()}`,
        role: "assistant",
        content:
          "Hello! I am Sherpa, your confidential AI wellbeing mentor. How are you feeling today?",
        flags: [],
        createdAt: new Date().toISOString(),
      },
    ]);
    setActiveScreen("chat");
  };

  const handleSend = async (textValue) => {
    const selectedPastSessionId = currentSession?.readOnly ? currentSession.id : null;
    const previousLiveChatId = sessions.find(
      (session) => !session.readOnly && session.remoteSessionId
    )?.remoteSessionId;
    let continuedChatId = null;
    try {
      await sendMessage(textValue, {
        onPastChatContinued: (remoteSessionId) => {
          continuedChatId = remoteSessionId;
        },
      });
      setDraft("");
    } catch (error) {
      toast.error(error?.message || "Unable to send message right now.");
    } finally {
      if (continuedChatId) {
        liveSessionIdRef.current = selectedPastSessionId;
        navigate("/sherpa", { replace: true });
        if (previousLiveChatId && previousLiveChatId !== continuedChatId) {
          endAiChat({ chatId: previousLiveChatId }).catch(() => {});
        }
        if (isMobile) setDrawerOpen(false);
      }
    }
  };

  const handleQuickReply = async (value) => {
    try {
      await sendMessage(value);
    } catch (error) {
      toast.error(error?.message || "Unable to send message right now.");
    }
  };

  const handleContinue = async () => {
    try {
      await continueConversation();
    } catch (error) {
      toast.error(error?.message || "Unable to continue session.");
    }
  };

  const handleEnd = async () => {
    try {
      await endConversation();
      handleNewChat();
    } catch (error) {
      toast.error(error?.message || "Unable to end session.");
    }
  };

  const handleResumeInterrupted = async () => {
    if (!interruptedChatId || isSending) return;
    setIsInterruptedPromptOpen(false);
    try {
      const newId = createNewSession({
        intent: "resume",
        resumeChatId: interruptedChatId,
      });
      const { remoteSessionId, sessionData, mappedMessages } =
        await resumeChat({ chatId: interruptedChatId });
      updateSessionMeta(newId, {
        remoteSessionId,
        dbSessionId:
          sessionData?.db_session_id || sessionData?.dbSessionId || null,
        idleTimeoutSeconds: sessionData?.idle_timeout_seconds || 1800,
      });
      updateSessionMessages(newId, mappedMessages);
      setInterruptedChatId(null);
      if (isMobile) setDrawerOpen(false);
    } catch (error) {
      toast.error(error?.message || "Unable to resume session.");
    }
  };

  const handleStartNewInstead = async () => {
    if (isSending) return;
    setIsInterruptedPromptOpen(false);
    setInterruptedChatId(null);
    try {
      const newId = createNewSession({ intent: "fresh" });
      const { remoteSessionId, sessionData, mappedMessages } =
        await startFreshChat();
      updateSessionMeta(newId, {
        remoteSessionId,
        dbSessionId:
          sessionData?.db_session_id || sessionData?.dbSessionId || null,
        idleTimeoutSeconds: sessionData?.idle_timeout_seconds || 1800,
      });
      updateSessionMessages(newId, mappedMessages);
    } catch (error) {
      toast.error(error?.message || "Unable to start Sherpa session.");
    }
  };

  const handleNewChat = async () => {
    if (isSending) return;
    try {
      const nextSessionId = createNewSession({ intent: "fresh" });
      const { remoteSessionId, sessionData, mappedMessages } =
        await startFreshChat();
      updateSessionMeta(nextSessionId, {
        remoteSessionId,
        dbSessionId:
          sessionData?.db_session_id || sessionData?.dbSessionId || null,
        idleTimeoutSeconds: sessionData?.idle_timeout_seconds || 1800,
      });
      updateSessionMessages(nextSessionId, mappedMessages);
      liveSessionIdRef.current = nextSessionId;
      await fetchArchivedSessions();
      setActiveScreen("chat");
      if (isMobile) setDrawerOpen(false);
    } catch (error) {
      toast.error(error?.message || "Unable to start new chat.");
    }
  };

  const handleSelectSession = async (id) => {
    if (isSending) return;
    const selected = sessions.find((session) => session.id === id);
    if (selected?.readOnly) {
      if (currentSession && !currentSession.readOnly) {
        liveSessionIdRef.current = currentSession.id;
      }
      try {
        await openArchivedSession(selected.archivedSessionId);
        navigate(`/sherpa/session/${selected.archivedSessionId}`);
        if (isMobile) setDrawerOpen(false);
      } catch (error) {
        toast.error(error?.message || "Unable to load past session.");
      }
      return;
    }
    selectSession(id);
    setActiveScreen("chat");
    if (isMobile) setDrawerOpen(false);
  };

  const handleReturnToLive = () => {
    const liveSessionId =
      liveSessionIdRef.current ||
      sessions.find((session) => !session.readOnly)?.id;
    if (liveSessionId) selectSession(liveSessionId);
    navigate("/sherpa");
  };

  const desktopSidebar = (
    <SherpaSidebar
      sessions={sessions.filter((session) => session.isArchived)}
      selectedSessionId={selectedSessionId}
      isLoadingSessions={isLoadingSessions}
      sessionsError={sessionsError}
      isOngoing={isSending}
      hasInterruptedSession={Boolean(interruptedChatId)}
      onResumeInterrupted={handleResumeInterrupted}
      onSelectSession={handleSelectSession}
      onNewSession={handleNewChat}
    />
  );

  const mobileSidebar = (
    <SherpaSidebar
      sessions={sessions.filter((session) => session.isArchived)}
      selectedSessionId={selectedSessionId}
      isLoadingSessions={isLoadingSessions}
      sessionsError={sessionsError}
      isOngoing={isSending}
      hasInterruptedSession={Boolean(interruptedChatId)}
      hideHeader
      onResumeInterrupted={handleResumeInterrupted}
      onSelectSession={handleSelectSession}
      onNewSession={handleNewChat}
    />
  );

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/home");
    }
  };

  const chatHeader = (
    <SherpaHeader
      onBack={handleBack}
      pastSessionId={currentSession?.readOnly ? currentSession.archivedSessionId : null}
      onReturnToLive={handleReturnToLive}
      onOpenDisclaimer={() => setIsDisclaimerModalOpen(true)}
      onOpenHistory={() => setDrawerOpen(true)}
    />
  );
  const sessionHistory = (
    <MobileSessionDrawer
      open={isDrawerOpen}
      onClose={() => setDrawerOpen(false)}
      onNewSession={handleNewChat}
      isNewSessionDisabled={isSending}
    >
      {mobileSidebar}
    </MobileSessionDrawer>
  );

  if (activeScreen === "loading") {
    return (
      <SherpaLayout
        appSidebar={<SidebarCol />}
        sidebar={desktopSidebar}
        header={chatHeader}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: "#0E8B63" }} />
          <Typography variant="body2" sx={{ color: "#5C7268" }}>
            Connecting with Sherpa...
          </Typography>
          {sessionHistory}
        </Box>
      </SherpaLayout>
    );
  }

  if (activeScreen === "error") {
    return (
      <SherpaLayout
        appSidebar={<SidebarCol />}
        sidebar={desktopSidebar}
        header={chatHeader}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            px: 3,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "#EAF8F2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2.5,
              color: "#0E8B63",
            }}
          >
            <CloudOffIcon sx={{ fontSize: 38 }} />
          </Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "#154C3A", mb: 1 }}
          >
            Coach Unavailable
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#6B7280", maxWidth: 450, mb: 3 }}
          >
            {initErrorMessage ||
              "Unable to connect to the chat server. Please check your connection and try again."}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="contained"
              onClick={handleRetry}
              sx={{
                backgroundColor: "#0E8B63",
                "&:hover": { backgroundColor: "#0B6B4C" },
                borderRadius: "10px",
                px: 3.5,
                py: 1.25,
              }}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              onClick={handleStartDemoMode}
              sx={{
                borderColor: "#0E8B63",
                color: "#0E8B63",
                "&:hover": {
                  borderColor: "#0B6B4C",
                  backgroundColor: "#F2FAF6",
                },
                borderRadius: "10px",
                px: 3,
                py: 1.25,
                fontWeight: 650,
              }}
            >
              Test in Local / Demo Mode
            </Button>
          </Stack>
          {sessionHistory}
        </Box>
      </SherpaLayout>
    );
  }

  if (activeScreen === "disclaimer") {
    return (
      <SherpaLayout
        appSidebar={<SidebarCol />}
        sidebar={desktopSidebar}
        header={chatHeader}
      >
        <SherpaDisclaimerView
          onBegin={handleBeginDisclaimer}
          isLoading={isSending}
        />
        {sessionHistory}
      </SherpaLayout>
    );
  }

  return (
    <SherpaLayout
      appSidebar={<SidebarCol />}
      sidebar={desktopSidebar}
      header={chatHeader}
      composer={
        <ChatInput
          value={draft}
          onChange={setDraft}
          onSend={handleSend}
          disabled={isSending || isLoadingSessionMessages}
          placeholder="Share your thoughts..."
        />
      }
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {isInterruptedPromptOpen && !currentSession?.readOnly ? (
          <Alert
            sx={{ mx: "auto", mt: 1, width: "100%", maxWidth: 1000 }}
            severity="info"
            action={
              <Stack direction="row" spacing={1}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleResumeInterrupted}
                >
                  Resume
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleStartNewInstead}
                >
                  Start New
                </Button>
              </Stack>
            }
          >
            You have a previous conversation in progress. Resume it or start a new
            one?
          </Alert>
        ) : null}
        {isCrisis ? (
          <Box
            sx={{
              mx: "auto",
              px: { xs: 2, sm: 3 },
              width: "100%",
              maxWidth: 1000,
            }}
          >
            <CrisisReferralCard onDismiss={dismissCrisis} />
          </Box>
        ) : null}
        {shouldClose && !currentSession?.readOnly ? (
          <Alert
            sx={{ mx: "auto", mt: 1, width: "100%", maxWidth: 1000 }}
            severity="warning"
            action={
              <Stack direction="row" spacing={1}>
                <Button color="inherit" size="small" onClick={handleContinue}>
                  Continue
                </Button>
                <Button color="inherit" size="small" onClick={handleEnd}>
                  End
                </Button>
              </Stack>
            }
          >
            Sherpa recommends wrapping up this session.
          </Alert>
        ) : null}
        <ChatMessageList
          messages={currentSession?.messages || []}
          isSending={isSending || isLoadingSessionMessages}
          onQuickReply={currentSession?.readOnly ? undefined : handleQuickReply}
        />
      </Box>
      <Dialog
        open={isDisclaimerModalOpen}
        onClose={() => setIsDisclaimerModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            maxHeight: "90vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#154C3A" }}>
            Terms of Use &amp; Disclaimer
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setIsDisclaimerModalOpen(false)}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflowY: "auto" }}>
          <SherpaDisclaimerView
            onBegin={() => setIsDisclaimerModalOpen(false)}
            isLoading={false}
            buttonLabel="Close"
          />
        </DialogContent>
      </Dialog>
      {sessionHistory}
    </SherpaLayout>
  );
}

export { ChatMain };
